import type { PortalDsBadgeVariant } from 'src/components/portal-design-system';
import { fnv1a32 } from '../utils';
import type { Client } from '../types/client.types';

export const CLIENT_AVATAR_TONES = ['blue', 'purple', 'green', 'amber', 'red'] as const;
export type ClientAvatarTone = (typeof CLIENT_AVATAR_TONES)[number];

export function getClientInitials(title: string): string {
  const trimmed = title.trim();
  return trimmed ? trimmed[0].toUpperCase() : '?';
}

export function getUserInitials(email: string): string {
  const local = email.split('@')[0] ?? email;
  return local.slice(0, 2).toUpperCase() || '?';
}

export function getAvatarTone(name: string): ClientAvatarTone {
  const key = name.trim().toLowerCase();
  const hash = fnv1a32(key);
  return CLIENT_AVATAR_TONES[hash % CLIENT_AVATAR_TONES.length];
}

export function getWebsiteLabel(website?: string | null): string {
  if (!website) {
    return 'No website added';
  }

  try {
    const url = new URL(/^https?:\/\//i.test(website) ? website : `https://${website}`);
    return url.hostname.replace(/^www\./, '');
  } catch {
    return website.replace(/^https?:\/\//i, '').replace(/^www\./, '').replace(/\/$/, '');
  }
}

export function getUniqueAccountCount(client: Client): number {
  return new Set((client.access ?? []).map((access) => access.account)).size;
}

export function getUniqueCampaignCount(client: Client): number {
  return new Set((client.access ?? []).map((access) => access.campaign)).size;
}

export function getClientStatus(client: Client): {
  label: string;
  variant: PortalDsBadgeVariant;
} {
  const hasUsers = (client.users?.length ?? 0) > 0;
  const hasAccess = (client.access?.length ?? 0) > 0;
  if (!hasUsers && !hasAccess) {
    return { label: 'Draft', variant: 'gray' };
  }
  return { label: 'Active', variant: 'blue' };
}

export function isClientStudioMigrated(client: Pick<Client, 'studio_migrated_at' | 'studio_ready'>): boolean {
  return Boolean(client.studio_migrated_at || client.studio_ready);
}
